import './SourceStamp.css';
import type { JSX } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { t } from '../../copy/strings';
import { fmtSections } from '../../format/numbers';
import { fmtTime } from '../../format/time';
import { epochMs, tMinOfIso } from '../../model/time';
import type { ResultsView } from '../../model/types';
import { useConfig, useStatics, useView } from '../../state/hooks';
import { placesAheadFraction } from './StatusIndicator';

export type SourceStampVariant = 'map' | 'inline' | 'tv' | 'footer';

/** Página de resultados do TSE (§6.5 bloco 5). */
export const TSE_RESULTS_URL = 'https://resultados.tse.jus.br/';

export interface SourceStampProps {
  /** A posição de cada variante é da caixa .app-shell__stamp / .app-shell__footer do AppShell (§4.3, §6.5). */
  variant: SourceStampVariant;
}

export type StampKind = 'noData' | 'tse' | 'simulation' | 'fixture';

export interface StampLine {
  kind: StampKind;
  /** Texto completo da linha 1. */
  text: string;
  /** "TSE" vira link (fora da simulação, dos dados de teste e da TV). */
  link: boolean;
  /** Trecho que some primeiro quando falta espaço (" · dados de 20h55"); "" sem hora. */
  droppable: string;
  /** Linha 1 dividida: antes do trecho que pode sumir / depois dele. */
  head: string;
  tail: string;
}

/**
 * Linha 1 do selo (§6.5 bloco 4): sem dados → "Fonte: TSE"; `tse` e `replay` → com a hora do snapshot
 * exibido; simulação e fixture sem link. Na TV, sem hora e sem link (a hora já está no status).
 */
export function stampLine(
  view: ResultsView | null,
  mode: 'live' | 'simulation',
  variant: SourceStampVariant,
  electionDate = '2026-10-25',
): StampLine {
  const origin = view?.origin ?? (mode === 'simulation' ? 'simulation' : null);
  if (origin === 'simulation' || origin === 'fixture') {
    const text = t(origin === 'simulation' ? 'source.stamp.simulation' : 'source.stamp.fixture');
    return { kind: origin, text, link: false, droppable: '', head: text, tail: '' };
  }
  const base = t('source.stamp.noData');
  const tv = variant === 'tv';
  if (!view?.tseHt || tv) return { kind: 'noData', text: base, link: !tv, droppable: '', head: base, tail: '' };
  const text = t('source.stamp', { HHhMM: fmtTime(tMinOfIso(view.tseHt, epochMs(electionDate)), electionDate) });
  // O trecho com a hora é o que sobra do texto completo depois do prefixo e do sufixo comuns com o texto sem dados.
  let suffix = 0;
  while (suffix < base.length && suffix < text.length && base.at(-1 - suffix) === text.at(-1 - suffix)) suffix++;
  let prefix = 0;
  while (prefix < base.length - suffix && base[prefix] === text[prefix]) prefix++;
  return {
    kind: 'tse',
    text,
    link: true,
    head: text.slice(0, prefix),
    droppable: text.slice(prefix, text.length - suffix),
    tail: text.slice(text.length - suffix),
  };
}

/** Trecho do texto do catálogo que vira link. */
const LINK_WORD = 'TSE';

/** "TSE" como link (nova aba), o resto como texto; o texto do link é o próprio trecho do catálogo (t()). */
function withLink(text: string, link: boolean): JSX.Element | string {
  const i = link ? text.indexOf(LINK_WORD) : -1;
  if (i < 0) return text;
  const end = i + LINK_WORD.length;
  return (
    <>
      {text.slice(0, i)}
      <a
        class="source-stamp__link"
        href={TSE_RESULTS_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('source.stamp.linkAria')}
      >
        {text.slice(i, end)}
      </a>
      {text.slice(end)}
    </>
  );
}

/**
 * Selo de fonte (§6.5, M3, M30): "Fonte: TSE · dados de HHhMM", sempre visível; "Fonte: TSE" nunca some ("site não
 * oficial" saiu: ORD-F-145). Variantes `map` (desktop, canto do mapa), `inline` (celular), `tv` (no TopBar) e
 * `footer` (rodapé de créditos do `flow`, dentro de `<footer role="contentinfo">`). Sem `aria-live`.
 */
export function SourceStamp({ variant }: SourceStampProps) {
  const view = useView();
  const cfg = useConfig();
  const geo = useStatics().geography;
  const line = stampLine(view, cfg.mode, variant, cfg.election_date);
  const ahead = placesAheadFraction(view, geo);
  const note = ahead === null ? null : t('source.placesAhead', { S: fmtSections(ahead) });
  const ref = useRef<HTMLParagraphElement>(null);

  // Variante map, falta de espaço (§6.5 bloco 6): até 2 linhas; se ainda não couber, some " · dados de {HHhMM}".
  // Observa o palco (.app-shell), não o selo nem a caixa dele: mudar o selo no callback não realimenta o observador.
  useLayoutEffect(() => {
    const el = ref.current;
    if (variant !== 'map' || !el) return;
    const refit = () => {
      el.removeAttribute('data-compact');
      const first = el.querySelector('.source-stamp__line');
      if (first && first.getClientRects().length > 2) el.setAttribute('data-compact', '');
    };
    refit();
    const stage = el.closest('.app-shell') ?? document.documentElement;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(refit);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [variant, line.text]);

  const lineOne = (
    <span class="source-stamp__line">
      {withLink(line.head, line.link)}
      {line.droppable && <span class="source-stamp__time">{line.droppable}</span>}
      {line.tail}
    </span>
  );

  if (variant === 'footer') {
    const photos = cfg.features.photos;
    // a frase das estimativas só com a chance no feed: com a chave ligada e o motor sem publicar, o site é o de hoje (P-87)
    const model = cfg.features.probability && (view?.probability ?? null) !== null;
    return (
      // o <footer role="contentinfo"> é a caixa .app-shell__footer do AppShell (§6.5 bloco 7; um só landmark)
      <div class="source-stamp source-stamp--footer" data-testid="source-stamp-footer">
        <p class="source-stamp__stamp" data-kind={line.kind}>
          {lineOne}
          {note && <span class="source-stamp__note">{note}</span>}
        </p>
        <p class="source-stamp__credits">
          {t('source.footer.results')}
          {photos && ` ${t('source.footer.photos')}`} {t('source.footer.maps')}
          {model && ` ${t('source.footer.model')}`}
        </p>
        <p class="source-stamp__disclaimer">
          {t('source.footer.disclaimer', { PRODUCT: import.meta.env.VITE_PRODUCT_NAME })}
        </p>
        <p class="source-stamp__links">
          <a href="/sobre">{t('source.footer.links.about')}</a>
          <span aria-hidden="true"> · </span>
          <a href="/metodologia">{t('source.footer.links.method')}</a>
          <span aria-hidden="true"> · </span>
          <a href="/privacidade">{t('source.footer.links.privacy')}</a>
          {/* FB-02 (PG-06 a): a entrada do site do replay; sem VITE_REPLAY_URL, não existe */}
          {import.meta.env.VITE_REPLAY_URL ? (
            <>
              <span aria-hidden="true"> · </span>
              <a href={import.meta.env.VITE_REPLAY_URL} data-testid="footer-replay-link">
                {t('nav.replay')}
              </a>
            </>
          ) : null}
          {/* ORD-F2-022 item 1.1 e D12: sem a variável (o padrão), não existem */}
          {import.meta.env.VITE_POLLS_URL ? (
            <>
              <span aria-hidden="true"> · </span>
              <a href={import.meta.env.VITE_POLLS_URL} data-testid="footer-polls-link">
                {t('nav.polls.aria')}
              </a>
            </>
          ) : null}
          {import.meta.env.VITE_CHANNEL_URL ? (
            <>
              <span aria-hidden="true"> · </span>
              <a
                href={import.meta.env.VITE_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-channel-link"
              >
                {t('source.footer.links.channel')}
              </a>
            </>
          ) : null}
        </p>
      </div>
    );
  }

  return (
    <p
      ref={ref}
      class={`source-stamp source-stamp--${variant}`}
      data-testid="source-stamp"
      data-variant={variant}
      data-kind={line.kind}
    >
      {lineOne}
      {note && variant !== 'tv' && <span class="source-stamp__note">{note}</span>}
    </p>
  );
}
